import {
  MissionDebugMap,
  MissionRegistryListener,
} from '../types/missionDebug'; // Updated import path

/**
 * Global registry for the debug state of the *currently active* mission component.
 * Implements a singleton pattern and a simple pub/sub mechanism.
 * Uses a "Last Registered Wins" strategy: assumes only one MissionDebugProvider
 * registers its map at a time, overwriting the previous one.
 */
class MissionDebugRegistry {
  private activeMap: MissionDebugMap | null = null;
  private listeners: Set<MissionRegistryListener> = new Set();
  private readonly shouldLog = process.env.NODE_ENV !== 'test';

  private notifyListeners(): void {
    // Notify listeners with the current active map
    this.listeners.forEach((listener) => listener(this.activeMap));
  }

  /**
   * Registers the debug map for a mission component, making it the active one.
   * Typically called when a MissionDebugProvider mounts.
   * Overwrites any previously active map.
   * @param map The MissionDebugMap from the mounting provider.
   */
  register(map: MissionDebugMap): void {
    if (this.shouldLog) {
      console.log('[MissionDebugRegistry] Registering new map:', map);
    }
    this.activeMap = map;
    this.notifyListeners();
  }

  /**
   * Unregisters the currently active debug map.
   * Typically called when the active MissionDebugProvider unmounts.
   * It only unregisters if the map being unregistered is the currently active one
   * (to prevent race conditions if components unmount out of order, though unlikely
   * with the page-level provider strategy).
   * @param map The map instance attempting to unregister.
   */
  unregister(map: MissionDebugMap): void {
    // Only clear the active map if the map being unregistered is the currently active one
    if (this.activeMap === map) {
      if (this.shouldLog) {
        console.log('[MissionDebugRegistry] Unregistering active map.');
      }
      this.activeMap = null;
      this.notifyListeners();
    } else {
      if (this.shouldLog) {
        console.log('[MissionDebugRegistry] Unregister called, but map was not the active one.');
      }
    }
  }

  /**
   * Gets the debug map of the currently active mission.
   * @returns The active MissionDebugMap or null if none is registered.
   */
  getActiveMap(): MissionDebugMap | null {
    return this.activeMap;
  }

  /**
   * Subscribes a listener function to changes in the active debug map.
   * The listener is called immediately with the current map upon subscription.
   * @param listener The function to call when the registry changes.
   * @returns An unsubscribe function.
   */
  subscribe(listener: MissionRegistryListener): () => void {
    this.listeners.add(listener);
    // Call listener immediately with current state
    listener(this.activeMap);
    // Return an unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }
}

// Export a singleton instance
const missionDebugRegistry = new MissionDebugRegistry();
export default missionDebugRegistry; 
